import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, MapPin, Phone, Mail, Save, Trash2, Search, ChevronDown, ChevronRight, Plus, RefreshCw, Loader2 } from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';
import { toast } from 'sonner';
import * as perms from '../lib/permissions';

// 50 states + DC
const US_STATES = [
  ['AL','Alabama'],   ['AK','Alaska'],    ['AZ','Arizona'],
  ['AR','Arkansas'],  ['CA','California'],['CO','Colorado'],
  ['CT','Connecticut'],['DE','Delaware'], ['DC','District of Columbia'],
  ['FL','Florida'],   ['GA','Georgia'],   ['HI','Hawaii'],
  ['ID','Idaho'],     ['IL','Illinois'],  ['IN','Indiana'],
  ['IA','Iowa'],      ['KS','Kansas'],    ['KY','Kentucky'],
  ['LA','Louisiana'], ['ME','Maine'],     ['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'], ['MT','Montana'],
  ['NE','Nebraska'],  ['NV','Nevada'],    ['NH','New Hampshire'],
  ['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],  ['OR','Oregon'],    ['PA','Pennsylvania'],
  ['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'], ['TX','Texas'],     ['UT','Utah'],
  ['VT','Vermont'],   ['VA','Virginia'],  ['WA','Washington'],
  ['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

const toE164 = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) return `+${trimmed.slice(1).replace(/\D/g, '')}`;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits.length >= 7 ? `+${digits}` : '';
};

const formatDisplay = (e164) => {
  if (!e164) return '';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164;
};

/**
 * Single-state expandable row. Only renders inputs when expanded so the page
 * stays fast even with all 51 states rendered at once.
 */
const EmailInboxSection = ({ code, api }) => {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/email/admin/inboxes/${code}`);
        setCfg(data);
      } catch {/* ignore */}
      try {
        const { data } = await api.get('/tasks/assignable-users');
        // Endpoint returns { users: [...] } — extract the array. The
        // previous code stored the wrapper object, then `users.map()`
        // crashed the page when it tried to render the user dropdown.
        const list = Array.isArray(data) ? data : (data?.users || data?.items || []);
        setUsers(list);
      } catch {/* ignore */}
    })();
  }, [code, api]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const body = {
        state: code,
        email_address: (cfg?.email_address || `${code.toLowerCase()}@collectiveorb.com`),
        admin_queue_enabled: cfg?.admin_queue_enabled ?? true,
        fallback_user_id: cfg?.fallback_user_id ?? null,
        ...patch,
      };
      await api.post('/email/admin/inboxes', body);
      const { data } = await api.get(`/email/admin/inboxes/${code}`);
      setCfg(data);
      toast.success('Email config saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  const queueOn = !!cfg.admin_queue_enabled;
  const fb = cfg.fallback_user_id || '';

  return (
    <div className="mt-2 pt-2 border-t border-dashed" data-testid={`state-email-section-${code}`}>
      <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
        Email Inbox Routing
      </p>
      <p className="text-[11px] text-muted-foreground mb-2">
        Shared inbox: <span className="font-mono">{cfg.email_address || `${code.toLowerCase()}@collectiveorb.com`}</span>
      </p>
      <div className="flex gap-2 flex-wrap mb-2">
        <button
          type="button"
          onClick={() => save({ admin_queue_enabled: !queueOn })}
          disabled={saving}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            queueOn
              ? 'bg-[#586D52] text-white border-[#586D52]'
              : 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a]'
          }`}
          data-testid={`state-email-queue-toggle-${code}`}
        >
          Admin Queue {queueOn ? 'ON' : 'OFF'}
        </button>
      </div>
      <label className="text-[11px] font-semibold text-muted-foreground">Fallback user for unmatched replies</label>
      <select
        value={fb}
        onChange={(e) => save({ fallback_user_id: e.target.value || null })}
        disabled={saving}
        className="mt-1 w-full h-8 rounded border border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#1a1a1a] text-xs px-2"
        data-testid={`state-email-fallback-${code}`}
      >
        <option value="">— None —</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>
            {`${u.first_name || ''} ${u.last_name || ''} (${u.email})`.trim()}
          </option>
        ))}
      </select>
      <p className="text-[11px] text-muted-foreground mt-1.5">
        Incoming replies with unknown tokens route to: {queueOn && fb ? 'admin queue + fallback user' : queueOn ? 'admin queue' : fb ? 'fallback user only' : 'nothing (dropped)'}.
      </p>
    </div>
  );
};

const StateRow = ({ code, name, settings, onSave, onDelete, onSaveType, openSignal, api, canEdit = true }) => {
  const hasAny = !!(settings?.phone_number || settings?.orb_address || settings?.orb_email);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(settings?.phone_number ? formatDisplay(settings.phone_number) : '');
  const [address, setAddress] = useState(settings?.orb_address || '');
  const [email, setEmail] = useState(settings?.orb_email || '');
  // state_type powers the restricted-state feature. Options:
  //   • "standard_internal" — existing cross-state behaviour
  //   • "strict_isolated"   — silo this state from every other one
  // Default to standard so rows without the field stay put.
  const [stateType, setStateType] = useState(settings?.state_type || 'standard_internal');
  const [saving, setSaving] = useState(false);
  const [savingType, setSavingType] = useState(false);

  useEffect(() => {
    setPhone(settings?.phone_number ? formatDisplay(settings.phone_number) : '');
    setAddress(settings?.orb_address || '');
    setEmail(settings?.orb_email || '');
    setStateType(settings?.state_type || 'standard_internal');
  }, [settings?.phone_number, settings?.orb_address, settings?.orb_email, settings?.state_type]);

  // Admin clicked "Add a State" → expand and scroll this row into view.
  useEffect(() => {
    if (!openSignal) return;
    setOpen(true);
    const el = document.querySelector(`[data-testid="state-row-${code}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-[#586D52]');
      setTimeout(() => el.classList.remove('ring-2', 'ring-[#586D52]'), 1600);
    }
  }, [openSignal, code]);

  const dirty = (
    (phone || '') !== (settings?.phone_number ? formatDisplay(settings.phone_number) : '') ||
    (address || '') !== (settings?.orb_address || '') ||
    (email || '') !== (settings?.orb_email || '')
  );

  const typeDirty = (stateType || 'standard_internal') !== (settings?.state_type || 'standard_internal');

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        phone_number: phone ? toE164(phone) : null,
        orb_address: (address || '').trim() || null,
        orb_email: (email || '').trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveType = async () => {
    setSavingType(true);
    try {
      await onSaveType(stateType);
    } finally {
      setSavingType(false);
    }
  };

  return (
    <div className={`rounded-xl border ${hasAny ? 'border-[#586D52]/30 bg-[#586D52]/5' : 'border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-[#1a1a1a]/60'}`} data-testid={`state-row-${code}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-2.5 text-left"
        data-testid={`state-toggle-${code}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
          <p className="font-medium text-sm">{name}</p>
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{code}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {settings?.phone_number && <span className="hidden sm:inline truncate max-w-[140px]" title={settings.phone_number}>{formatDisplay(settings.phone_number)}</span>}
          {(settings?.state_type === 'strict_isolated') && (
            <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 uppercase tracking-wider text-[10px] font-semibold" data-testid={`state-type-badge-${code}`}>
              Restricted
            </span>
          )}
          {hasAny && <span className="px-1.5 py-0.5 rounded bg-[#586D52]/15 text-[#586D52] uppercase tracking-wider text-[10px]">configured</span>}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="relative">
              <Phone className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (704) 555-1212"
                className="h-9 text-sm pl-8"
                data-testid={`state-phone-${code}`}
                disabled={!canEdit}
              />
            </div>
            <div className="relative">
              <Mail className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="state@orb.com"
                className="h-9 text-sm pl-8"
                data-testid={`state-email-${code}`}
                disabled={!canEdit}
              />
            </div>
          </div>
          <div className="relative">
            <MapPin className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Business mailing address"
              className="h-9 text-sm pl-8"
              data-testid={`state-address-${code}`}
              disabled={!canEdit}
            />
          </div>
          {canEdit ? (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !dirty}
              className="bg-[#586D52] hover:bg-[#4a5d46] h-8"
              data-testid={`state-save-${code}`}
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              Save
            </Button>
            {hasAny && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50 h-8"
                onClick={() => onDelete()}
                disabled={saving}
                data-testid={`state-delete-${code}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>
          ) : (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 italic pt-1">
              Read-only. Standard state details can only be edited by a full admin.
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Saving cascades to every user in this state — they'll see this as their ORB Phone, ORB Email, and ORB Address.
          </p>

          {/* State operating mode ------------------------------------
              Separate save button so admins can toggle the mode
              without having to simultaneously re-save the phone/
              email/address fields. */}
          <div className="mt-2 pt-2 border-t border-dashed">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
              Operating mode
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setStateType('standard_internal')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  stateType === 'standard_internal'
                    ? 'bg-[#586D52] text-white border-[#586D52]'
                    : 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a] hover:border-[#586D52]'
                }`}
                data-testid={`state-type-standard-${code}`}
              >
                Standard State
              </button>
              <button
                type="button"
                onClick={() => setStateType('strict_isolated')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  stateType === 'strict_isolated'
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a] hover:border-red-500'
                }`}
                data-testid={`state-type-restricted-${code}`}
              >
                Restricted State
              </button>
              {typeDirty && (
                <Button
                  size="sm"
                  onClick={saveType}
                  disabled={savingType}
                  className="bg-[#586D52] hover:bg-[#4a5d46] h-8"
                  data-testid={`state-type-save-${code}`}
                >
                  <Save className="w-3.5 h-3.5 mr-1" />
                  Apply
                </Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {stateType === 'strict_isolated'
                ? 'Restricted: users here are siloed from other states — no cross-state chat, meetings, or visibility.'
                : 'Standard: existing cross-state team behaviour (conversations, meetings, admin visibility).'}
            </p>
          </div>
          <EmailInboxSection code={code} api={api} />
        </div>
      )}
    </div>
  );
};


const StatewideDetailsPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [byState, setByState] = useState({});
  const [search, setSearch] = useState('');
  // Two-tab header: Standard (incl. exemptions) vs Restricted.
  const [stateTab, setStateTab] = useState('standard');
  // Add-a-state flow: admin types a state name + picks a mode. Adding
  // creates the row in state_phone_numbers (if new), then expands
  // & scrolls that row into view via the openSignals map.
  const [openSignals, setOpenSignals] = useState({});
  const [addName, setAddName] = useState('');
  const [addMode, setAddMode] = useState('standard_internal');
  const [adding, setAdding] = useState(false);

  // Email-webhooks resync popover state (header icon button)
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [phoneWebhookBusy, setPhoneWebhookBusy] = useState(false);
  const [phoneWebhookResult, setPhoneWebhookResult] = useState(null);
  // Messaging (SMS) webhooks share the same Twilio endpoint as the
  // voice webhooks — `configure_number_webhooks` writes BOTH voice_url
  // and sms_url in one update — but the admin asked for a distinct
  // section/button so the messaging-route sync is visible at a glance.
  const [msgWebhookBusy, setMsgWebhookBusy] = useState(false);
  const [msgWebhookResult, setMsgWebhookResult] = useState(null);

  const isAdmin = user?.role === 'admin';

  const runResubscribe = useCallback(async () => {
    setWebhookBusy(true);
    setWebhookResult(null);
    try {
      const res = await api.post('/email/admin/graph/resubscribe');
      const data = res.data || {};
      const created = (data.created || []).length;
      const errors = (data.delete_errors || []).length;
      setWebhookResult({
        ok: !!data.ok,
        created,
        errors,
        deleteErrors: data.delete_errors || [],
      });
      if (errors === 0) toast.success(`Resubscribed ${created} state mailboxes`);
      else toast.warning(`Resubscribed ${created} mailboxes — ${errors} error(s)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Resubscribe failed';
      toast.error(msg);
      setWebhookResult({ ok: false, error: msg });
    } finally {
      setWebhookBusy(false);
    }
  }, [api]);

  const runPhoneSync = useCallback(async () => {
    setPhoneWebhookBusy(true);
    setPhoneWebhookResult(null);
    try {
      const res = await api.post('/admin/twilio/sync-all-webhooks');
      const data = res.data || {};
      const sum = data.summary || {};
      // Detect production lockdown: every row will carry `skipped:true`
      // with reason `twilio_writes_locked_to_production_only`. Surface
      // that distinctly so the admin understands nothing was actually
      // written when running from preview.
      const allRows = [...(data.state || []), ...(data.individual || []), ...(data.group || [])];
      const locked = allRows.length > 0 && allRows.every((r) => r.skipped && r.reason === 'twilio_writes_locked_to_production_only');
      if (locked) {
        const host = allRows[0]?.pod_hostname || '(unknown)';
        setPhoneWebhookResult({ ok: false, locked: true, pod_hostname: host });
        toast.warning('Twilio writes are locked to production — nothing written from this environment.');
      } else if ((sum.failed || 0) === 0) {
        setPhoneWebhookResult({ ok: true, ok_count: sum.ok || 0 });
        toast.success(`Synced ${sum.ok || 0} Twilio number(s)`);
      } else {
        setPhoneWebhookResult({ ok: false, ok_count: sum.ok || 0, fail_count: sum.failed || 0 });
        toast.warning(`Synced ${sum.ok || 0} · ${sum.failed} failed`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Sync failed';
      toast.error(msg);
      setPhoneWebhookResult({ ok: false, error: msg });
    } finally {
      setPhoneWebhookBusy(false);
    }
  }, [api]);

  // Messaging (SMS) webhooks are written in the same Twilio API call
  // as voice webhooks via `configure_number_webhooks`, so this button
  // reuses the same backend endpoint. We track its busy/result state
  // separately so the admin can run + see results for messaging
  // independently of the phone sync.
  const runMessagingSync = useCallback(async () => {
    setMsgWebhookBusy(true);
    setMsgWebhookResult(null);
    try {
      const res = await api.post('/admin/twilio/sync-all-webhooks');
      const data = res.data || {};
      const sum = data.summary || {};
      const allRows = [...(data.state || []), ...(data.individual || []), ...(data.group || [])];
      const locked = allRows.length > 0 && allRows.every((r) => r.skipped && r.reason === 'twilio_writes_locked_to_production_only');
      if (locked) {
        const host = allRows[0]?.pod_hostname || '(unknown)';
        setMsgWebhookResult({ ok: false, locked: true, pod_hostname: host });
        toast.warning('Twilio writes are locked to production — nothing written from this environment.');
      } else if ((sum.failed || 0) === 0) {
        setMsgWebhookResult({ ok: true, ok_count: sum.ok || 0 });
        toast.success(`Messaging webhooks synced for ${sum.ok || 0} Twilio number(s)`);
      } else {
        setMsgWebhookResult({ ok: false, ok_count: sum.ok || 0, fail_count: sum.failed || 0 });
        toast.warning(`Messaging webhooks synced ${sum.ok || 0} · ${sum.failed} failed`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Sync failed';
      toast.error(msg);
      setMsgWebhookResult({ ok: false, error: msg });
    } finally {
      setMsgWebhookBusy(false);
    }
  }, [api]);

  // No back arrow per user request — Statewide Details lives in the
  // admin hub and is reached deliberately. On admin we add a header
  // rightSlot with a sync icon that opens the Email Webhooks resync
  // popover (mirrors the in-page card we used to render at the top).
  usePageHeader({
    title: 'Statewide Details',
    rightSlot: isAdmin ? (
      <button
        type="button"
        onClick={() => setWebhookOpen(true)}
        className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center text-[#586D52]"
        aria-label="Webhooks"
        title="Webhooks"
        data-testid="email-webhooks-header-btn"
      >
        <RefreshCw className="w-5 h-5" />
      </button>
    ) : null,
  }, [isAdmin]);

  const sd = perms.statewideDetails(user);
  const canAccess = sd.view;

  const load = async () => {
    try {
      const res = await api.get('/twilio/admin/state-settings');
      const map = {};
      for (const it of res.data?.items || []) {
        map[(it.state || '').toUpperCase()] = it;
      }
      setByState(map);
    } catch (e) {
      toast.error('Could not load statewide details');
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (code, payload) => {
    try {
      const res = await api.put(`/twilio/admin/state-settings/${encodeURIComponent(code)}`, payload);
      const twilio = res?.data?.twilio;
      if (twilio?.ok) {
        toast.success(`${code} saved · phone + messaging webhooks synced`);
      } else if (twilio?.skipped) {
        toast.success(`${code} saved · Twilio webhooks locked to production`);
      } else if (twilio && !twilio.ok) {
        toast.warning(`${code} saved · Twilio webhook sync failed: ${twilio.error || 'unknown'}`);
      } else {
        toast.success(`${code} saved & cascaded to users`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Clear ${code} statewide details? Users currently assigned this number will have ORB Phone cleared.`)) return;
    try {
      await api.delete(`/twilio/admin/phone-numbers/${encodeURIComponent(code)}`);
      toast.success(`${code} cleared`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear');
    }
  };

  /** PUT /twilio/admin/phone-numbers/{code}/type — flips this state
   *  between Standard and Restricted modes. Auto-upserts if the row
   *  doesn't exist yet (admin can register a brand-new state + mode
   *  in one click). */
  const handleSaveType = async (code, state_type) => {
    try {
      await api.put(`/twilio/admin/phone-numbers/${encodeURIComponent(code)}/type`, { state_type });
      toast.success(
        state_type === 'strict_isolated'
          ? `${code} is now a Restricted State`
          : `${code} is now a Standard State`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update state mode');
    }
  };

  /** Register a brand-new "state" — the input can be ANY name (US state,
   *  region, country, custom label). The backend normalizes US state
   *  names to their 2-letter code (e.g. "Texas" → "TX") and stores
   *  everything else verbatim (upper-cased, e.g. "Canada" → "CANADA").
   *  After the row exists we scroll to it and expand it so the admin
   *  can add phone / email / address in one flow. */
  const handleAddState = async () => {
    const raw = (addName || '').trim();
    if (!raw) { toast.error('Enter a state name'); return; }
    const sent = raw.toUpperCase();
    setAdding(true);
    try {
      const res = await api.put(
        `/twilio/admin/phone-numbers/${encodeURIComponent(sent)}/type`,
        { state_type: addMode }
      );
      // Use the backend-normalized code for the scroll/expand target so
      // "Texas" correctly opens the existing TX row instead of creating
      // a bogus "TEXAS" signal that never fires.
      const storedCode = (res?.data?.state || sent).toUpperCase();
      toast.success(
        addMode === 'strict_isolated'
          ? `${storedCode} added as a Restricted State`
          : `${storedCode} added as a Standard State`
      );
      await load();
      setSearch('');
      setOpenSignals((m) => ({ ...m, [storedCode]: (m[storedCode] || 0) + 1 }));
      setAddName('');
      setAddMode('standard_internal');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add state');
    } finally {
      setAdding(false);
    }
  };

  const filteredStates = useMemo(() => {
    // Start with every US state + DC (always-visible baseline), then
    // append any custom state the admin has registered that is NOT in
    // the US list (e.g. "CANADA", "EU", "REGION-1"). Custom states are
    // sorted by code so the list stays deterministic.
    const usCodes = new Set(US_STATES.map(([c]) => c));
    const customPairs = Object.keys(byState)
      .filter((c) => !usCodes.has(c))
      .sort()
      .map((c) => [c, c]); // For custom codes, display = code itself
    const all = [...US_STATES, ...customPairs];
    // Tab filter: Standard tab shows non-restricted states; Restricted
    // tab shows the strict_isolated ones. Pristine US-state rows with
    // no row yet default to standard.
    const wantRestricted = stateTab === 'restricted';
    const byTab = all.filter(([code]) => {
      const st = byState[code]?.state_type || 'standard_internal';
      return wantRestricted ? st !== 'standard_internal' : st === 'standard_internal';
    });
    const q = search.trim().toLowerCase();
    if (!q) return byTab;
    return byTab.filter(([code, name]) =>
      code.toLowerCase().includes(q) || name.toLowerCase().includes(q)
    );
  }, [search, byState, stateTab]);

  const restrictedStateCodes = useMemo(() => {
    const s = new Set();
    for (const [code, info] of Object.entries(byState)) {
      if ((info?.state_type || 'standard_internal') !== 'standard_internal') s.add(code);
    }
    return s;
  }, [byState]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <p className="text-muted-foreground text-sm mt-1">You don't have access to this page.</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="statewide-details-page">
      <div className="max-w-3xl mx-auto p-3">
        <Card className="rounded-2xl mb-3">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            Set the <strong>ORB Phone</strong>, <strong>ORB Email</strong>, and <strong>ORB Address</strong> for each state.
            Saving any field cascades to every user in that state — they'll see it on their profile and business card. Click a state to expand.
          </CardContent>
        </Card>

        {/* Email webhooks resync — now lives in the page header
            (sync icon to the left of the notification bell). The
            dialog at the bottom of this page renders the popup. */}

        {/* Add-a-state CTA ——— free-text: admin can register ANY name
            (US state, region, country, custom label). Whatever is typed
            is stored verbatim as the `state` value on the row. */}
        <Card className="rounded-2xl mb-3 border-dashed" data-testid="add-state-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-2">
              <Plus className="w-4 h-4 text-[#586D52]" />
              <p className="text-sm font-semibold">Add a state</p>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Enter <strong>any</strong> name — a US state, region, country, or custom label — and choose its operating mode. Whatever you type is stored as the state on the row below so you can add phone, email, and address next.
            </p>
            <div className="flex flex-col gap-2">
              <Input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !adding) handleAddState(); }}
                placeholder="State name (e.g. Texas, Canada, EU-Region-1)"
                className="h-9 text-sm"
                data-testid="add-state-input"
              />
              <div className="flex gap-2 flex-wrap items-center">
                <button
                  type="button"
                  onClick={() => setAddMode('standard_internal')}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    addMode === 'standard_internal'
                      ? 'bg-[#586D52] text-white border-[#586D52]'
                      : 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a] hover:border-[#586D52]'
                  }`}
                  data-testid="add-state-mode-standard"
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode('strict_isolated')}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    addMode === 'strict_isolated'
                      ? 'bg-red-500 text-white border-red-500'
                      : 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a] hover:border-red-500'
                  }`}
                  data-testid="add-state-mode-restricted"
                >
                  Restricted
                </button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  onClick={handleAddState}
                  disabled={adding || !addName.trim()}
                  className="bg-[#586D52] hover:bg-[#4a5d46] h-9"
                  data-testid="add-state-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> {adding ? 'Adding…' : 'Add'}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {addMode === 'strict_isolated'
                  ? 'Restricted: users in this state are siloed — no cross-state chat, meetings, or visibility.'
                  : 'Standard: existing cross-state team behaviour.'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl mb-3">
          <CardContent className="pt-3 pb-3">
            <div className="flex gap-2 flex-wrap" data-testid="state-tabs">
              <Button size="sm" variant={stateTab === 'standard' ? 'default' : 'outline'} onClick={() => setStateTab('standard')} className={stateTab === 'standard' ? 'bg-[#586D52] hover:bg-[#4a5d46]' : ''} data-testid="state-tab-standard">Standard States</Button>
              <Button size="sm" variant={stateTab === 'restricted' ? 'default' : 'outline'} onClick={() => setStateTab('restricted')} className={stateTab === 'restricted' ? 'bg-red-500 hover:bg-red-600' : ''} data-testid="state-tab-restricted">Restricted States</Button>
            </div>
          </CardContent>
        </Card>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search states"
            className="pl-9"
            data-testid="state-search-input"
          />
        </div>

        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{stateTab === 'restricted' ? 'Restricted States' : 'Standard States'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {filteredStates.map(([code, name]) => (
              <StateRow
                key={code}
                code={code}
                name={name}
                settings={byState[code]}
                openSignal={openSignals[code] || 0}
                onSave={(p) => handleSave(code, p)}
                onDelete={() => handleDelete(code)}
                onSaveType={(t) => handleSaveType(code, t)}
                api={api}
                canEdit={sd.editStateDetails({
                  is_restricted: (byState[code]?.state_type || 'standard_internal') !== 'standard_internal',
                })}
              />
            ))}
          </CardContent>
        </Card>

        {stateTab === 'standard' && (
          <ExceptionsSection api={api} restrictedStateCodes={restrictedStateCodes} />
        )}
      </div>

      {/* Email Webhooks resync dialog — opened by the sync icon in the
          page header (rightSlot). Mirrors the content the in-page
          card used to render, with a single "Run" button. */}
      <Dialog open={webhookOpen} onOpenChange={setWebhookOpen}>
        <DialogContent className="max-w-md" data-testid="webhooks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#586D52]" />
              Webhooks
            </DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">
              Run these <strong>AFTER</strong> changing the production domain to re-point every webhook against the new URL.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Email Webhooks</p>
              <Button
                onClick={runResubscribe}
                disabled={webhookBusy}
                className="bg-[#586D52] hover:bg-[#4a5d44] text-white w-full"
                data-testid="email-webhooks-run-btn"
              >
                {webhookBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running… (~1 min)
                  </>
                ) : (
                  'Run'
                )}
              </Button>
              {webhookResult && webhookResult.ok && (
                <p className="text-xs text-green-700 dark:text-green-400" data-testid="email-webhooks-result">
                  ✓ Recreated {webhookResult.created} subscription(s).
                  {webhookResult.errors > 0 && (
                    <span className="block text-amber-600 mt-1">
                      {webhookResult.errors} delete error(s).
                    </span>
                  )}
                </p>
              )}
              {webhookResult && !webhookResult.ok && (
                <p className="text-xs text-red-600" data-testid="email-webhooks-error">
                  ✗ {webhookResult.error}
                </p>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-semibold text-muted-foreground">Phone Webhooks</p>
              <Button
                onClick={runPhoneSync}
                disabled={phoneWebhookBusy}
                className="bg-[#586D52] hover:bg-[#4a5d44] text-white w-full"
                data-testid="phone-webhooks-run-btn"
              >
                {phoneWebhookBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running…
                  </>
                ) : (
                  'Run'
                )}
              </Button>
              {phoneWebhookResult && phoneWebhookResult.locked && (
                <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="phone-webhooks-locked">
                  🔒 Twilio writes are locked to production. Nothing was written from this environment ({phoneWebhookResult.pod_hostname}).
                </p>
              )}
              {phoneWebhookResult && phoneWebhookResult.ok && !phoneWebhookResult.locked && (
                <p className="text-xs text-green-700 dark:text-green-400" data-testid="phone-webhooks-result">
                  ✓ Synced {phoneWebhookResult.ok_count} Twilio number(s).
                </p>
              )}
              {phoneWebhookResult && !phoneWebhookResult.ok && !phoneWebhookResult.locked && (
                <p className="text-xs text-red-600" data-testid="phone-webhooks-error">
                  ✗ {phoneWebhookResult.error || `${phoneWebhookResult.fail_count} failed`}
                </p>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-semibold text-muted-foreground">Messaging Webhooks</p>
              <p className="text-[11px] text-muted-foreground">
                Pushes the inbound SMS URL onto every Twilio number under our control (state pool + exemptions). Twilio updates voice + SMS in a single API call, so this writes the same record as the phone sync above.
              </p>
              <Button
                onClick={runMessagingSync}
                disabled={msgWebhookBusy}
                className="bg-[#586D52] hover:bg-[#4a5d44] text-white w-full"
                data-testid="msg-webhooks-run-btn"
              >
                {msgWebhookBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running…
                  </>
                ) : (
                  'Run'
                )}
              </Button>
              {msgWebhookResult && msgWebhookResult.locked && (
                <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="msg-webhooks-locked">
                  🔒 Twilio writes are locked to production. Nothing was written from this environment ({msgWebhookResult.pod_hostname}).
                </p>
              )}
              {msgWebhookResult && msgWebhookResult.ok && !msgWebhookResult.locked && (
                <p className="text-xs text-green-700 dark:text-green-400" data-testid="msg-webhooks-result">
                  ✓ Messaging webhooks pushed for {msgWebhookResult.ok_count} Twilio number(s).
                </p>
              )}
              {msgWebhookResult && !msgWebhookResult.ok && !msgWebhookResult.locked && (
                <p className="text-xs text-red-600" data-testid="msg-webhooks-error">
                  ✗ {msgWebhookResult.error || `${msgWebhookResult.fail_count} failed`}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =================== Exceptions section ===================
// Two subsections: Individual (per-user override) + Group (shared inbox).
// Editing happens in a Dialog modal so layout/scroll on long pages can
// never hide controls (previously caused a perceived "page goes black"
// on the production Statewide Details page when the inline form
// expanded off-screen).
const ExceptionsSection = ({ api, restrictedStateCodes }) => {
  const [tab, setTab] = useState('individual');
  const [users, setUsers] = useState([]);
  const [individualExempts, setIndividualExempts] = useState([]);
  const [groups, setGroups] = useState([]);
  // iter175ba — New Exception modal state. Drives the unified create
  // flow that replaces the two inline forms (Individual + Group). The
  // admin first chooses a kind (individual / group), then picks
  // user(s), then fills in phone / email / address.
  const [newExc, setNewExc] = useState(null); // null | { kind, user_id, user_ids[], name, orb_phone, orb_email, orb_address }
  const [newExcUserQuery, setNewExcUserQuery] = useState('');

  // Dialog edit state. `editIndividual.additional_user_ids` lets admins
  // share the exemption with extra members — on save it converts to a
  // Group exemption (carrying over the same name/phone/email/address)
  // and clears the original Individual row.
  const [editIndividual, setEditIndividual] = useState(null);
  const [editGroup, setEditGroup] = useState(null);
  const [saving, setSaving] = useState(false);

  // Restricted states are completely walled off — their users MUST NOT
  // appear in any exemption picker (individual dropdown / group members /
  // additional-user checkboxes). Falling back to an empty Set means we
  // simply show every user when the prop isn't passed (e.g. tests).
  const restricted = restrictedStateCodes || new Set();
  const eligibleUsers = useMemo(
    () => (users || []).filter((u) => !restricted.has((u.state || '').toUpperCase())),
    [users, restricted]
  );

  const reload = async () => {
    try {
      const [u, ie, g] = await Promise.all([
        api.get('/users/all'),
        api.get('/admin/exemptions/users'),
        api.get('/admin/exemptions/groups'),
      ]);
      setUsers(u.data || []);
      setIndividualExempts(ie.data?.items || []);
      setGroups(g.data?.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line

  const userById = (id) => (users || []).find((u) => u.id === id);

  const syncWebhooksAfterSave = async () => {
    // Fire-and-forget. The Twilio sync writes voice + SMS URLs onto
    // every number in our pool — important after a NEW exemption phone
    // is added — but it doesn't need to block the UI's reload. Email
    // graph resubscribe used to live here too; it was awaited and
    // takes ~60s, which made every exemption save APPEAR to fail
    // (the saved row was already in the DB but the list never
    // refreshed until the 60s graph call returned). Email
    // resubscribe is now triggered from the dedicated Webhooks
    // dialog only — not from per-row saves.
    api.post('/admin/twilio/sync-all-webhooks').catch(() => {});
  };

  const saveNewException = async () => {
    if (!newExc) return;
    setSaving(true);
    try {
      if (newExc.kind === 'individual') {
        if (!newExc.user_id) { setSaving(false); return toast.error('Pick a user'); }
        await api.put('/admin/exemptions/individual', {
          user_id: newExc.user_id,
          name: newExc.name || '',
          orb_phone: newExc.orb_phone || '',
          orb_email: newExc.orb_email || '',
          orb_address: newExc.orb_address || '',
        });
        toast.success('Individual exemption created');
      } else {
        if (!newExc.name) { setSaving(false); return toast.error('Group name required'); }
        if (!newExc.user_ids?.length) { setSaving(false); return toast.error('Pick at least one member'); }
        await api.post('/admin/exemptions/groups', {
          name: newExc.name,
          user_ids: newExc.user_ids,
          fallback_user_id: newExc.fallback_user_id || null,
          phone_number: newExc.orb_phone || null,
          orb_email: newExc.orb_email || null,
          orb_address: newExc.orb_address || null,
        });
        toast.success('Group exemption created');
      }
      setNewExc(null);
      setNewExcUserQuery('');
      await syncWebhooksAfterSave();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveEditedIndividual = async () => {
    if (!editIndividual) return;
    setSaving(true);
    try {
      const additional = editIndividual.additional_user_ids || [];
      if (additional.length > 0) {
        // Upgrade Individual → Group exemption sharing the same metadata.
        const allMembers = Array.from(new Set([editIndividual.user_id, ...additional]));
        await api.post('/admin/exemptions/groups', {
          name: editIndividual.name || (userById(editIndividual.user_id)?.full_name || 'Shared exemption'),
          user_ids: allMembers,
          fallback_user_id: editIndividual.user_id,
          phone_number: editIndividual.orb_phone || null,
          orb_email: editIndividual.orb_email || null,
          orb_address: editIndividual.orb_address || null,
        });
        // Clear the original Individual row so the number ONLY routes
        // through the new Group exemption.
        await api.delete(`/admin/exemptions/individual/${editIndividual.user_id}`);
        toast.success('Converted to group exemption');
      } else {
        await api.put('/admin/exemptions/individual', {
          user_id: editIndividual.user_id,
          name: editIndividual.name,
          orb_phone: editIndividual.orb_phone,
          orb_email: editIndividual.orb_email,
          orb_address: editIndividual.orb_address,
        });
        toast.success('Saved');
      }
      setEditIndividual(null);
      await syncWebhooksAfterSave();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveEditedGroup = async () => {
    if (!editGroup) return;
    setSaving(true);
    try {
      await api.put(`/admin/exemptions/groups/${editGroup.id}`, {
        name: editGroup.name || '',
        user_ids: editGroup.user_ids || [],
        fallback_user_id: editGroup.fallback_user_id || null,
        phone_number: editGroup.phone_number || null,
        orb_email: editGroup.orb_email || null,
        orb_address: editGroup.orb_address || null,
      });
      toast.success('Saved');
      setEditGroup(null);
      await syncWebhooksAfterSave();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const clearIndividual = async (uid) => {
    if (!window.confirm('Clear this user\'s exemption? They will fall back to the statewide values.')) return;
    try { await api.delete(`/admin/exemptions/individual/${uid}`); toast.success('Cleared'); await reload(); }
    catch (e) { toast.error('Could not clear'); }
  };

  // Helper: a memoized, reusable checkbox list for user selection. A
  // checkbox UX is dramatically more discoverable than a native multi-
  // select (`<select multiple>`) which requires ctrl/cmd-click.
  const UserCheckboxList = ({ selected, onToggle, exclude, testid }) => {
    const items = eligibleUsers.filter((u) => !exclude || !exclude.has(u.id));
    if (items.length === 0) {
      return <p className="text-[11px] text-muted-foreground italic">No eligible users.</p>;
    }
    return (
      <div className="max-h-48 overflow-y-auto rounded border border-input bg-background divide-y divide-gray-100 dark:divide-gray-800" data-testid={testid}>
        {items.map((u) => {
          const checked = selected.includes(u.id);
          return (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(u.id)}
                className="w-4 h-4 accent-[#586D52]"
                data-testid={`${testid || 'user-cb'}-${u.id}`}
              />
              <span className="flex-1 truncate">
                <strong>{u.full_name || u.email}</strong>
                <span className="ml-1 text-muted-foreground">({u.state || '—'})</span>
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  // Shared row UI for both Individual and Group exception lists.
  // Same visual treatment per the user's request: a soft amber-tinted
  // card with consistent meta-line layout (phone / email / address)
  // and right-aligned Edit + Clear/Delete actions. Single component
  // means future tweaks stay in lockstep across both lists.
  const ExceptionRow = ({ kind, title, subtitle, phone, email, address, onEdit, onClear, clearLabel, testid }) => (
    <div
      className="rounded-lg p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 text-xs flex items-center gap-2"
      data-testid={testid}
    >
      <span className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-[#586D52]/10 text-[#586D52] dark:bg-[#586D52]/30 dark:text-[#c8d6c0]">
            {kind === 'individual' ? 'Indiv' : 'Group'}
          </span>
          <strong className="truncate">{title}</strong>
          {subtitle && <span className="text-muted-foreground truncate">· {subtitle}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
          {phone && <span>📞 {phone}</span>}
          {email && <span>✉ {email}</span>}
          {address && <span>📍 {address}</span>}
        </div>
      </span>
      <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`${testid}-edit`}>Edit</Button>
      <Button size="sm" variant="ghost" onClick={onClear} data-testid={`${testid}-clear`}>{clearLabel || 'Clear'}</Button>
    </div>
  );

  // iter175bo — Admin must see EVERY individual exemption regardless
  // of the user's home state. The previous defensive filter
  //   .filter((u) => !restricted.has((u.state || '').toUpperCase()))
  // hid exemption rows whenever the user's state happened to be
  // empty AND the state-settings map contained a malformed
  // `""`-keyed row marked non-standard (real-world cause: standalone
  // users with no state assigned). Result: the exemption persisted
  // in the DB and worked on the user's profile + business card, but
  // the admin couldn't see or manage it from this UI. We trust the
  // backend's `/admin/exemptions/users` response now — it already
  // filters out group members — and surface every row it returns.
  const visibleIndividualExempts = useMemo(
    () => (individualExempts || []),
    [individualExempts]
  );
  // iter175bo — Same as visibleIndividualExempts above. Trust the
  // backend response — surface every group exemption so admins can
  // see + manage them even when membership intersects an unusual
  // state setup.
  const visibleGroups = useMemo(
    () => (groups || []),
    [groups]
  );

  return (
    <>
    <Card className="rounded-2xl mt-3" data-testid="exceptions-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">Exceptions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex gap-2">
            <Button size="sm" variant={tab === 'individual' ? 'default' : 'outline'} onClick={() => setTab('individual')} data-testid="exception-tab-individual">Individual</Button>
            <Button size="sm" variant={tab === 'group' ? 'default' : 'outline'} onClick={() => setTab('group')} data-testid="exception-tab-group">Group</Button>
          </div>
          <Button
            size="sm"
            className="bg-[#586D52] hover:bg-[#4a5d46] text-white"
            onClick={() => setNewExc({ kind: null, user_id: '', user_ids: [], name: '', orb_phone: '', orb_email: '', orb_address: '', fallback_user_id: '' })}
            data-testid="new-exception-btn"
          >
            + New Exception
          </Button>
        </div>

        {tab === 'individual' && (
          <div className="space-y-1.5" data-testid="individual-list">
            {visibleIndividualExempts.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No individual exceptions yet.</p>
            ) : visibleIndividualExempts.map((u) => (
              <ExceptionRow
                key={u.id}
                kind="individual"
                title={u.exemption_name || u.full_name || u.email}
                subtitle={u.exemption_name && (u.full_name || u.email) ? (u.full_name || u.email) : null}
                phone={u.exemption?.phone ? u.orb_phone : null}
                email={u.exemption?.email ? u.orb_email : null}
                address={u.exemption?.address ? u.orb_address : null}
                onEdit={() => setEditIndividual({
                  user_id: u.id,
                  name: u.exemption_name || '',
                  // iter151 — only autofill exemption field values
                  // whose `exemption.<field>` flag is actually true.
                  // Previously we read the raw orb_phone/email/address
                  // off the user doc, which let zombie data (left
                  // over from a prior Clear) pre-fill the dialog and
                  // re-create an exemption on Save.
                  orb_phone: u.exemption?.phone ? (u.orb_phone || '') : '',
                  orb_email: u.exemption?.email ? (u.orb_email || '') : '',
                  orb_address: u.exemption?.address ? (u.orb_address || '') : '',
                  additional_user_ids: [],
                })}
                onClear={() => clearIndividual(u.id)}
                testid={`exempt-row-${u.id}`}
              />
            ))}
          </div>
        )}

        {tab === 'group' && (
          <div className="space-y-1.5" data-testid="group-list">
            {visibleGroups.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No group exceptions yet.</p>
            ) : visibleGroups.map((g) => (
              <ExceptionRow
                key={g.id}
                kind="group"
                title={g.name}
                subtitle={`${(g.user_ids || []).length} member${g.user_ids?.length === 1 ? '' : 's'} · fallback: ${userById(g.fallback_user_id)?.full_name || '—'}`}
                phone={g.phone_number}
                email={g.orb_email}
                address={g.orb_address}
                onEdit={() => setEditGroup({
                  id: g.id,
                  name: g.name || '',
                  user_ids: g.user_ids || [],
                  fallback_user_id: g.fallback_user_id || '',
                  phone_number: g.phone_number || '',
                  orb_email: g.orb_email || '',
                  orb_address: g.orb_address || '',
                })}
                onClear={async () => {
                  if (window.confirm('Delete this group?')) {
                    try { await api.delete(`/admin/exemptions/groups/${g.id}`); toast.success('Deleted'); reload(); }
                    catch (e) { toast.error('Failed'); }
                  }
                }}
                clearLabel="Delete"
                testid={`group-row-${g.id}`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/* ===== New Exception Dialog (iter175ba) =====
         Unified create flow that replaced the two inline forms.
         Step 1: Pick kind (individual | group).
         Step 2: Pick user(s) — searchable.
         Step 3: Fill in phone / email / address (any subset). ===== */}
    <Dialog open={!!newExc} onOpenChange={(v) => { if (!v) { setNewExc(null); setNewExcUserQuery(''); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-exception-dialog">
        <DialogHeader>
          <DialogTitle>New exception</DialogTitle>
          <DialogDescription>
            Override a user's (or group's) ORB phone, email, or address.
          </DialogDescription>
        </DialogHeader>
        {newExc && (
          <div className="space-y-3">
            {/* Kind selector */}
            <div>
              <p className="text-[11px] font-semibold mb-1">Type</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  size="sm"
                  variant={newExc.kind === 'individual' ? 'default' : 'outline'}
                  className={newExc.kind === 'individual' ? 'bg-[#586D52] hover:bg-[#4a5d46] text-white' : ''}
                  onClick={() => setNewExc((p) => p && { ...p, kind: 'individual' })}
                  data-testid="new-exc-kind-individual"
                >
                  Individual
                </Button>
                <Button
                  size="sm"
                  variant={newExc.kind === 'group' ? 'default' : 'outline'}
                  className={newExc.kind === 'group' ? 'bg-[#586D52] hover:bg-[#4a5d46] text-white' : ''}
                  onClick={() => setNewExc((p) => p && { ...p, kind: 'group' })}
                  data-testid="new-exc-kind-group"
                >
                  Group
                </Button>
              </div>
            </div>

            {newExc.kind === 'individual' && (
              <>
                <div>
                  <p className="text-[11px] font-semibold mb-1">User</p>
                  <Input
                    placeholder="Search by name or email…"
                    value={newExcUserQuery}
                    onChange={(e) => setNewExcUserQuery(e.target.value)}
                    className="text-sm mb-1"
                    data-testid="new-exc-user-search"
                  />
                  <div className="max-h-44 overflow-y-auto rounded border border-input bg-background divide-y divide-gray-100 dark:divide-gray-800" data-testid="new-exc-user-list">
                    {eligibleUsers
                      .filter((u) => {
                        if (!newExcUserQuery.trim()) return true;
                        const q = newExcUserQuery.trim().toLowerCase();
                        return (
                          (u.full_name || '').toLowerCase().includes(q) ||
                          (u.email || '').toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 100)
                      .map((u) => {
                        const sel = newExc.user_id === u.id;
                        return (
                          <label
                            key={u.id}
                            className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer text-xs ${sel ? 'bg-[#586D52]/10' : 'hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'}`}
                          >
                            <input
                              type="radio"
                              name="new-exc-user"
                              checked={sel}
                              onChange={() => setNewExc((p) => p && { ...p, user_id: u.id })}
                              className="w-4 h-4 accent-[#586D52]"
                              data-testid={`new-exc-user-${u.id}`}
                            />
                            <span className="flex-1 truncate">
                              <strong>{u.full_name || u.email}</strong>
                              <span className="ml-1 text-muted-foreground">({u.role || '—'} · {u.state || '—'})</span>
                            </span>
                          </label>
                        );
                      })}
                    {eligibleUsers.length === 0 && (
                      <p className="text-[11px] text-muted-foreground italic px-2 py-2">No eligible users.</p>
                    )}
                  </div>
                </div>
                <Input
                  placeholder="Display name (free-form, optional)"
                  value={newExc.name}
                  onChange={(e) => { const v = e.target.value; setNewExc((p) => p && { ...p, name: v }); }}
                  className="text-sm"
                  data-testid="new-exc-name"
                />
              </>
            )}

            {newExc.kind === 'group' && (
              <>
                <div>
                  <p className="text-[11px] font-semibold mb-1">Group name</p>
                  <Input
                    value={newExc.name}
                    onChange={(e) => { const v = e.target.value; setNewExc((p) => p && { ...p, name: v }); }}
                    className="text-sm"
                    data-testid="new-exc-group-name"
                  />
                </div>
                <div>
                  <p className="text-[11px] font-semibold mb-1">Members</p>
                  <Input
                    placeholder="Search by name or email…"
                    value={newExcUserQuery}
                    onChange={(e) => setNewExcUserQuery(e.target.value)}
                    className="text-sm mb-1"
                    data-testid="new-exc-member-search"
                  />
                  <div className="max-h-44 overflow-y-auto rounded border border-input bg-background divide-y divide-gray-100 dark:divide-gray-800" data-testid="new-exc-member-list">
                    {eligibleUsers
                      .filter((u) => {
                        if (!newExcUserQuery.trim()) return true;
                        const q = newExcUserQuery.trim().toLowerCase();
                        return (
                          (u.full_name || '').toLowerCase().includes(q) ||
                          (u.email || '').toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 200)
                      .map((u) => {
                        const checked = (newExc.user_ids || []).includes(u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] cursor-pointer text-xs">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setNewExc((p) => {
                                if (!p) return p;
                                const cur = p.user_ids || [];
                                return { ...p, user_ids: cur.includes(u.id) ? cur.filter((x) => x !== u.id) : [...cur, u.id] };
                              })}
                              className="w-4 h-4 accent-[#586D52]"
                              data-testid={`new-exc-member-${u.id}`}
                            />
                            <span className="flex-1 truncate">
                              <strong>{u.full_name || u.email}</strong>
                              <span className="ml-1 text-muted-foreground">({u.role || '—'} · {u.state || '—'})</span>
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
                {(newExc.user_ids || []).length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold mb-1">Fallback recipient (must be a member)</p>
                    <select
                      value={newExc.fallback_user_id || ''}
                      onChange={(e) => { const v = e.target.value; setNewExc((p) => p && { ...p, fallback_user_id: v }); }}
                      className="w-full h-9 text-sm rounded border border-input bg-background px-2"
                      data-testid="new-exc-fallback"
                    >
                      <option value="">— select fallback —</option>
                      {(newExc.user_ids || []).map((uid) => { const u = userById(uid); return u ? <option key={uid} value={uid}>{u.full_name || u.email}</option> : null; })}
                    </select>
                  </div>
                )}
              </>
            )}

            {newExc.kind && (
              <>
                <div className="grid grid-cols-1 gap-2 pt-1 border-t border-gray-200 dark:border-gray-800">
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">ORB Phone (E.164)</p>
                    <Input
                      placeholder="+15551234567"
                      value={newExc.orb_phone}
                      onChange={(e) => { const v = e.target.value; setNewExc((p) => p && { ...p, orb_phone: v }); }}
                      className="text-sm"
                      data-testid="new-exc-phone"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">ORB Email</p>
                    <Input
                      placeholder="name@example.com"
                      value={newExc.orb_email}
                      onChange={(e) => { const v = e.target.value; setNewExc((p) => p && { ...p, orb_email: v }); }}
                      className="text-sm"
                      data-testid="new-exc-email"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">ORB Address</p>
                    <Input
                      placeholder="123 Main St, City, ST"
                      value={newExc.orb_address}
                      onChange={(e) => { const v = e.target.value; setNewExc((p) => p && { ...p, orb_address: v }); }}
                      className="text-sm"
                      data-testid="new-exc-address"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => { setNewExc(null); setNewExcUserQuery(''); }} disabled={saving}>Cancel</Button>
              <Button
                size="sm"
                onClick={saveNewException}
                disabled={saving || !newExc.kind || (newExc.kind === 'individual' && !newExc.user_id) || (newExc.kind === 'group' && (!newExc.name || (newExc.user_ids || []).length === 0))}
                className="bg-[#586D52] hover:bg-[#4a5d46] text-white"
                data-testid="new-exc-save"
              >
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving…' : 'Create exception'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* ===== Individual Edit Dialog — hoisted outside the Card so any
              re-renders inside the section never affect the portal. ===== */}
    <Dialog open={!!editIndividual} onOpenChange={(v) => { if (!v) setEditIndividual(null); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="exempt-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit individual exemption</DialogTitle>
          <DialogDescription>
            {editIndividual && (userById(editIndividual.user_id)?.full_name || userById(editIndividual.user_id)?.email)}
          </DialogDescription>
        </DialogHeader>
        {editIndividual && (
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-muted-foreground mb-1">Display name (free-form)</p>
              <Input
                value={editIndividual.name || ''}
                onChange={(e) => { const v = e.target.value; setEditIndividual((p) => p && { ...p, name: v }); }}
                className="text-sm"
                data-testid="exempt-edit-name"
              />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Private ORB Phone (E.164)</p>
                <Input value={editIndividual.orb_phone || ''} onChange={(e) => { const v = e.target.value; setEditIndividual((p) => p && { ...p, orb_phone: v }); }} className="text-sm" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">ORB Email</p>
                <Input value={editIndividual.orb_email || ''} onChange={(e) => { const v = e.target.value; setEditIndividual((p) => p && { ...p, orb_email: v }); }} className="text-sm" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">ORB Address</p>
                <Input value={editIndividual.orb_address || ''} onChange={(e) => { const v = e.target.value; setEditIndividual((p) => p && { ...p, orb_address: v }); }} className="text-sm" />
              </div>
            </div>
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-2 space-y-1">
              <p className="text-[11px] font-semibold">Share with additional users (converts to a group)</p>
              <p className="text-[11px] text-muted-foreground">Check anyone who should also ring on this number. Saving will convert the exemption into a Group.</p>
              <UserCheckboxList
                selected={editIndividual.additional_user_ids || []}
                onToggle={(uid) => setEditIndividual((p) => {
                  if (!p) return p;
                  const cur = p.additional_user_ids || [];
                  return { ...p, additional_user_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] };
                })}
                exclude={new Set([editIndividual.user_id])}
                testid="exempt-edit-additional"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => setEditIndividual(null)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={saveEditedIndividual} disabled={saving} className="bg-[#586D52]" data-testid="exempt-edit-save">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* ===== Group Edit Dialog (hoisted outside the Card). ===== */}
    <Dialog open={!!editGroup} onOpenChange={(v) => { if (!v) setEditGroup(null); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit group exemption</DialogTitle>
        </DialogHeader>
        {editGroup && (
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-muted-foreground mb-1">Group name</p>
              <Input value={editGroup.name} onChange={(e) => { const v = e.target.value; setEditGroup((p) => p && { ...p, name: v }); }} className="text-sm" />
            </div>
            <div>
              <p className="text-[11px] font-semibold mb-1">Members</p>
              <UserCheckboxList
                selected={editGroup.user_ids}
                onToggle={(uid) => setEditGroup((p) => {
                  if (!p) return p;
                  const cur = p.user_ids || [];
                  return { ...p, user_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] };
                })}
                testid="group-edit-members"
              />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground mb-1">Fallback recipient</p>
              <select value={editGroup.fallback_user_id || ''} onChange={(e) => { const v = e.target.value; setEditGroup((p) => p && { ...p, fallback_user_id: v }); }} className="w-full h-9 text-sm rounded border border-input bg-background px-2">
                <option value="">— select fallback —</option>
                {(editGroup.user_ids || []).map((uid) => { const u = userById(uid); return u ? <option key={uid} value={uid}>{u.full_name || u.email}</option> : null; })}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Group ORB Phone</p>
                <Input value={editGroup.phone_number || ''} onChange={(e) => { const v = e.target.value; setEditGroup((p) => p && { ...p, phone_number: v }); }} className="text-sm" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Group ORB Email</p>
                <Input value={editGroup.orb_email || ''} onChange={(e) => { const v = e.target.value; setEditGroup((p) => p && { ...p, orb_email: v }); }} className="text-sm" />
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Group ORB Address</p>
                <Input value={editGroup.orb_address || ''} onChange={(e) => { const v = e.target.value; setEditGroup((p) => p && { ...p, orb_address: v }); }} className="text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => setEditGroup(null)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={saveEditedGroup} disabled={saving} className="bg-[#586D52]" data-testid="group-edit-save">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default StatewideDetailsPage;
